import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Box, Card, CardContent, Typography, TextField, Button } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import { loginWithEmail } from "service/axiosConfig";

function SignInPage() {
  const [email, setEmail] = useState("");
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email) {
      alert("Please enter your email.");
      return;
    }
    await loginWithEmail(email);
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        background: "linear-gradient(to bottom right, #fff7f0, #ffffff, #f0fff7)",
        fontFamily:
          'var(--default-font-family, ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji")',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          borderBottom: "1px solid #e2e8f0",
          bgcolor: "rgba(255,255,255,0.8)",
          backdropFilter: "blur(4px)",
        }}
      >
        <Box
          sx={{
            maxWidth: "1200px",
            mx: "auto",
            px: 2,
            py: 1,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          {/* Heartline clickable logo */}
          <Box
            onClick={() => (window.location.href = "https://myheartline.com")}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              cursor: "pointer",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 32,
                height: 32,
                bgcolor: "#7e2a0c",
                borderRadius: "12px",
              }}
            >
              {/* Client’s Heart SVG */}
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="white"
                stroke="white"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{
                  width: "20px",
                  height: "20px",
                  display: "block",
                  verticalAlign: "middle",
                }}
              >
                <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z"></path>
              </svg>
            </Box>
            <Typography
              fontWeight="bold"
              sx={{
                fontFamily: "inherit",
                fontSize: "20px",
                color: "#0f172a",
              }}
            >
              Heartline
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Main Content */}
      <Box sx={{ maxWidth: 416, mx: "auto", px: 2, py: 6 }}>
        <Box textAlign="center" mb={3}>
          <Typography
            fontWeight="bold"
            variant="h1"
            gutterBottom
            sx={{
              fontFamily: "inherit",
              fontSize: "1.875rem", // smaller than before
              color: "#0f172a",
            }}
          >
            Sign In to Heartline
          </Typography>
          <Typography
            sx={{
              fontFamily: "inherit",
              fontSize: "16px",
              color: "#475569",
            }}
          >
            Enter your email and we&apos;ll send you a magic link to log in securely.
          </Typography>
        </Box>

        <Card
          variant="outlined"
          sx={{
            borderColor: "#fed7aa",
            borderWidth: 1,
            textAlign: "center",
            borderRadius: "calc(var(--radius, 6px) + 4px)",
            boxShadow: "none",
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "3rem",
                  height: "3rem",
                  bgcolor: "#ffedd5",
                  borderRadius: "16px",
                }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  style={{
                    width: "24px", // same as w-6
                    height: "24px",
                    color: "#9a3412", // matches text-orange-800
                    display: "block",
                    verticalAlign: "middle",
                  }}
                >
                  <rect width="20" height="16" x="2" y="4" rx="2" />
                  <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
                </svg>
              </Box>
            </Box>

            <Typography
              fontWeight="400"
              variant="h4"
              sx={{
                mb: 2,
                fontFamily: "inherit",
                fontSize: "1.25rem",
                color: "#0f172a",
              }}
            >
              Magic Link Login
            </Typography>

            <form onSubmit={handleLogin}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {/* Label */}
                <Typography
                  component="label"
                  htmlFor="email"
                  sx={{
                    fontWeight: 600,
                    fontSize: "0.875rem", // 14px
                    fontFamily: "inherit",
                    color: "#0f172a",
                    textAlign: "left", // left aligned
                  }}
                >
                  Email Address
                </Typography>

                {/* Input */}
                <Box
                  component="input"
                  id="email"
                  name="email"
                  type="email"
                  placeholder="Enter your email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  sx={{
                    fontSize: "0.875rem",
                    fontFamily: "inherit",
                    padding: "10px 12px",
                    backgroundColor: "#f8fafc",
                    border: "1px solid #e2e8f0", // default light border
                    borderRadius: "calc(var(--radius, 6px) + 4px)",
                    outline: "none",
                    width: "100%",
                    color: "#0f172a",
                    transition: "border-color 0.2s, box-shadow 0.2s",
                    "&:focus": {
                      borderColor: "#7c2d12", // your brand orange/brown
                      boxShadow: "0 0 0 1px #7c2d12", // subtle highlight
                    },
                  }}
                />
              </Box>

              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{
                  mt: 2,
                  bgcolor: "#7c2d12",
                  "&:hover": { bgcolor: "#65200d", color: "#ffffff" },
                  py: 0.6, // tighter
                  fontWeight: 600,
                  fontFamily: "inherit",
                  textTransform: "none",
                  color: "#ffffff",
                  fontSize: "14px",
                  borderRadius: "calc(var(--radius, 6px) + 4px)",
                  fontFamily: "inherit",
                  boxShadow: "none",
                }}
              >
                Send Magic Link
              </Button>

              <Typography
                sx={{
                  mt: 1.5,
                  fontFamily: "inherit",
                  fontSize: "0.875rem",
                  color: "#475569",
                }}
              >
                Your magic link will remain active for 30 days.
              </Typography>
            </form>
          </CardContent>
        </Card>

        {/* New User */}
        <Box
          sx={{
            textAlign: "center",
            mt: 3,
            p: 2,
            bgcolor: "#f8fafc",
            borderRadius: "calc(var(--radius, 6px) + 4px)",
          }}
        >
          <Typography
            sx={{
              fontFamily: "inherit",
              fontSize: "16px",
              color: "#475569",
            }}
          >
            New here?{" "}
            <Link
              to="/authentication/sign-up"
              style={{
                color: "#7c2d12",
                fontWeight: "600",
                textDecoration: "none",
                fontFamily: "inherit",
              }}
            >
              Register
            </Link>
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

export default SignInPage;
